<script setup lang="ts">
import {
  useDialog,
  useUser,
  ref,
  useToast,
  useMetrics,
  useFollowers,
  callOnce,
  type StylesElementVersion,
  watch,
} from "#imports";

import MPAuth from "../dialogs/MPAuth/MPAuth.vue";
import MPButton from "../forms/MPButton.vue";

const props = defineProps<{
  creatorId: string;
  iconOnly?: boolean;
  size?: "small" | "medium" | "large";
  bgColor?: string;
  version?: StylesElementVersion;
}>();

const emits = defineEmits<{
  creatorFollowed: [];
}>();

const { openDialog } = useDialog();
const { showError } = useToast();
const { trackEvent } = useMetrics();

const userStore = useUser();
const followersStore = useFollowers();
const isLoading = ref(false);

// fetch only once ever
await callOnce(() => followersStore.fetchFollowers());
watch(
  () => userStore.user.id,
  () => followersStore.fetchFollowers(),
);

const isFollowing = followersStore.isFollower(props.creatorId);

const addFollow = async () => {
  if (!userStore.user.id) {
    trackEvent("joined", {
      friction: "join_prompt_on_spot_card_follow_button",
    });
    openDialog(MPAuth, { startOn: "login" });
    return;
  }

  try {
    isLoading.value = true;

    await followersStore.addFollow(props.creatorId);
    emits("creatorFollowed");
  } catch (e) {
    showError("Unable to add follow", e);
  } finally {
    isLoading.value = false;
  }
};

const removeFollow = async () => {
  try {
    isLoading.value = true;
    await followersStore.removeFollow(props.creatorId);
  } catch (e) {
    showError("Unable to remove follow", e);
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <MPButton
    v-if="iconOnly && !isFollowing"
    class="follow-button"
    label="Follow Creator"
    hideLabel
    :backgroundColor="props.bgColor"
    :size="props.size"
    :disabled="isLoading"
    icon="ri-notification-3-line"
    @click="addFollow"
  />
  <MPButton
    v-else-if="iconOnly && isFollowing"
    class="follow-button"
    label="Unfollow Creator"
    hideLabel
    :backgroundColor="props.bgColor"
    :size="props.size"
    :disabled="isLoading"
    icon="ri-notification-3-fill"
    @click="removeFollow"
  />
  <MPButton
    v-else-if="!iconOnly && !isFollowing"
    :label="isLoading ? 'Loading...' : 'Follow Creator'"
    class="follow-button"
    :version="props.version || 'default'"
    icon="ri-notification-3-line"
    :size="props.size"
    :backgroundColor="props.bgColor"
    :disabled="isLoading"
    aria-label="Follow Creator"
    @click="addFollow"
  />
  <MPButton
    v-else-if="!iconOnly && isFollowing"
    :label="isLoading ? 'Loading...' : 'Unfollow Creator'"
    class="follow-button"
    icon="ri-notification-3-fill"
    :version="props.version || 'default'"
    :disabled="isLoading"
    :size="props.size"
    :backgroundColor="props.bgColor"
    aria-label="Unfollow Creator"
    @click="removeFollow"
  />
</template>
